import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { get as _get, includes as _includes, reduce as _reduce } from 'lodash';
import { take } from 'rxjs/operators';

import {
  BillAccount,
  BillAccountData,
  BillingPreferences,
  DeliveryPreferences,
  NewBillingPreference,
  PolicyList,
  Preferences,
  UpdatePreferences,
  UpdatePreferencesRequest
} from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';

@Injectable()
export class BillingUtilService {
  constructor(private store: Store) {}

  public buildPreferencePayload(
    newBillingPrerence: NewBillingPreference,
    billAccountData: BillAccountData,
    correlationId: string
  ): UpdatePreferencesRequest {
    const billingPreferences: BillingPreferences = billAccountData.billingPreferences;
    const newPreference = newBillingPrerence.preference;
    const billAccountNumber = newBillingPrerence.billAccountNumber;
    const preferencesObj: UpdatePreferencesRequest = {
      billAccountNumber: billAccountNumber,
      updatePreferenceCorrelationId: correlationId,
      associated: billAccountData.associated,
      billingMethod: billAccountData.billingMethod,
      payload: {
        billingPreference: {
          // spread operator to append accountNickName and dueDateReminder when available.
          ...(_get(billingPreferences, 'accountNickName')
            ? { accountNickname: _get(billingPreferences, 'accountNickName') }
            : {}),
          ...(_get(billingPreferences, 'dueDateReminder')
            ? { dueDateReminder: _get(billingPreferences, 'dueDateReminder') }
            : {}),
          preferences: this.preferenceBuilder(newPreference, billingPreferences.preferences)
        }
      }
    };
    return preferencesObj;
  }

  /**
   * @author: Abhishek Singh
   * @description: Takes 2 arrays of type PolicyList as input, and returns a single array of unique
   * data from the 2. lodash's _merge/_uniq could not be applied here as it supports merging of arrays and
   * not collections.
   */
  public uniqPolicyList(incomingPolicyList: PolicyList, policyList: PolicyList): PolicyList {
    return _reduce(
      policyList,
      function (result, value) {
        const policyItem = incomingPolicyList.find(policyObj =>
          _includes(policyObj.policyNumber, value.policyNumber)
        );
        if (policyItem) {
          result.push(Object.assign({}, policyItem, value));
        } else result.push(value);
        return result;
      },
      []
    );
  }

  /**
   * @author: Abhishek Singh
   * @description: Gets the list of billaccounts and creates an array of billing preference
   * to be used for setting the preference of the bill accounts.
   */
  public setBillAccountsToPaperless(billAccounts: BillAccount[]): NewBillingPreference[] {
    const newBillingPreferences: NewBillingPreference[] = [];
    billAccounts.forEach(billAccount => {
      _get(billAccount, 'billingPreferences.preferences', []).forEach(preference => {
        if (
          preference.isModifiable &&
          preference.preferenceDeliveryCode.toLowerCase() !== DeliveryPreferences.EMAIL
        ) {
          newBillingPreferences.push({
            billAccountNumber: billAccount.billAccountNumber,
            preference: DeliveryPreferences.EMAIL
          });
        }
      });
    });
    return newBillingPreferences;
  }
  /**
   * @param newPreference is modified preference
   * @param preferences is existing preferences
   * @param newPreferenceIsBoth tells selected preference is both(email and paper)
   * Payload required for API is built with above parameters.
   */
  private preferenceBuilder(
    newPreference: string,
    preferences: Preferences[],
    newPreferenceIsBoth?
  ): UpdatePreferences[] {
    if (newPreference === DeliveryPreferences.BOTH) {
      return [
        this.preferenceBuilder(DeliveryPreferences.PAPER, preferences, true)[0],
        this.preferenceBuilder(DeliveryPreferences.EMAIL, preferences, true)[0]
      ];
    } else {
      const pref = newPreference.toUpperCase();
      let originalPreference: Preferences;
      // Only to or from 'both' will have an existing preference, so we only look for it in those cases
      if (newPreferenceIsBoth || preferences.length > 1) {
        originalPreference = this.getExistingPreference(newPreference, preferences);
      }
      const preferenceInfo: UpdatePreferences = {
        payloadEntityId: 'DOCUMENTS.' + pref,
        preferenceCode: 'DOCUMENTS',
        preferenceDeliveryCode: pref,
        businessUnitCode: 'BILLING',
        preferenceTypeCode: 'DOCUMENT-NOTIF',
        lastUpdateTimestamp: _get(originalPreference, 'lastUpdateTimestamp', ''),
        phoneNumber: _get(originalPreference, 'phoneNumber', ''),
        preferenceId: _get(originalPreference, 'preferenceId', '')
      };
      if (newPreference === DeliveryPreferences.EMAIL) {
        const emailAddress = _get(originalPreference, 'emailAddress', this.getEmailAddress());
        preferenceInfo.emailAddress = emailAddress;
      }
      return [preferenceInfo];
    }
  }

  private getExistingPreference(newPreference: string, preferences: Preferences[]): Preferences {
    return preferences.find(pref => pref.preferenceDeliveryCode === newPreference.toUpperCase());
  }

  private getEmailAddress(): string {
    let emailAddress: string;
    this.store
      .select(userQuery.selectUserState)
      .pipe(take(1))
      .subscribe(currentUser => {
        emailAddress = currentUser.emailAddress;
      });
    return emailAddress;
  }
}
